import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { Box, Button, IconFilter } from '@machobear/design-system'

import { breakpoints, zIndex } from '@/styles'
import { useFilters } from '@/hooks/use-filters'
import { AlertButtonList } from '@/features/Listings/buttons'
import { useFiltersModal } from '@/features/Listings/hooks/use-filters-modal'

import ActiveFilterIndicators from './active-filter-indicators'
import FilterItem from './filter-item'
import BedBathFilter from './popovers/bed-bath-filter'
import DealsFilter from './popovers/deals-filter'
import FurnishingFilter from './popovers/furnishing-filter'
import PriceFilter from './popovers/price-filter'
import { disableBedrooms, getFilterValues } from './utils'

const Wrapper = styled.div`
  z-index: ${zIndex.searchFilters};
  position: relative;
  width: 100%;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    z-index: unset;
    overflow: hidden;
  }
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const hideListViewFilters = css`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const SecondaryButtons = styled.div`
  display: flex;
  margin-left: auto;
  gap: 12px;
`

const ListingFilter = ({
  isMapMode,
  hideAlertButton,
  hideResultsCount,
  boundaries,
  hideActiveFilters,
  isExt,
  isUniversityPage,
  centerPoint,
  companyId,
  extCompanyId
}) => {
  const { t } = useTranslation()
  const [filters, setFilter] = useFilters()
  const filterValues = getFilterValues(filters)
  const [showFiltersModal] = useFiltersModal({
    isMapMode,
    boundaries,
    hideResultsCount,
    isUniversityPage,
    centerPoint,
    companyId,
    extCompanyId
  })

  const clickId = 'map-filters-open-click'

  const handleClick = () => {
    window.dataLayer.push({
      event: clickId
    })
    showFiltersModal()
  }

  const setFilterMerging = newFilter => {
    const filterParams = { ...filterValues, ...newFilter, page: null }
    setFilter(filterParams)
  }

  const priceFilters = isUniversityPage
    ? { ...filters, search_only_verified: true }
    : filters

  const priceFilter = () => (
    <FilterItem
      title={t('filters:b.listing_filter.edit.price.label', 'Price')}
      customStyle={hideListViewFilters}
    >
      <PriceFilter
        companyId={companyId}
        extCompanyId={extCompanyId}
        savedFilters={priceFilters}
        value={{
          min_price: filters.min_price,
          max_price: filters.max_price
        }}
        onChange={({ min_price, max_price }) =>
          setFilterMerging({ min_price, max_price })
        }
        boundaries={boundaries}
        isExt={isExt}
      />
    </FilterItem>
  )

  const bedBathFilter = () => (
    <FilterItem
      title={t('filters:b.listing_filter.edit.bed_bath.label', 'Bed & Bath')}
      customStyle={hideListViewFilters}
    >
      <BedBathFilter
        value={{
          bedroom_count: filters.bedroom_count,
          bathroom_count: filters.bathroom_count
        }}
        disableBed={disableBedrooms(filters.unit_types)}
        onChange={({ bedroom_count, bathroom_count }) =>
          setFilterMerging({ bedroom_count, bathroom_count })
        }
        isExt={isExt}
      />
    </FilterItem>
  )

  const furnishingFilter = () => (
    <FilterItem
      title={t('filters:b.listing_filter.edit.furnishing.label', 'Furnishing')}
      customStyle={hideListViewFilters}
    >
      <FurnishingFilter
        features={filters.features}
        onChange={({ features }) => setFilterMerging({ features })}
        isExt={isExt}
      />
    </FilterItem>
  )

  const dealsFilter = () => (
    <FilterItem
      title={t('filters:b.listing_filter.edit.deals.label', 'Deals')}
      customStyle={hideListViewFilters}
    >
      <DealsFilter
        deals={filters.deals}
        onChange={({ deals }) => setFilterMerging({ deals })}
        isExt={isExt}
      />
    </FilterItem>
  )

  return (
    <Wrapper>
      <Content>
        <Box display={{ mobile: 'none', tablet: 'block' }}>
          <Button
            variant="ghost"
            icon={<IconFilter />}
            id={clickId}
            onClick={handleClick}
          >
            {t('filters:b.listing_filter.edit.filters.label', 'Filters')}
          </Button>
        </Box>
        {priceFilter()}
        {bedBathFilter()}
        {furnishingFilter()}
        {dealsFilter()}
        <SecondaryButtons>
          {!hideAlertButton && <AlertButtonList />}
        </SecondaryButtons>
      </Content>
      {!hideActiveFilters && (
        <ActiveFilterIndicators
          filters={filters}
          onChange={setFilterMerging}
          isMapMode={isMapMode}
          isUniversityPage={isUniversityPage}
        />
      )}
    </Wrapper>
  )
}

export default ListingFilter
